import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ProfessionalLayout from '../components/ProfessionalLayout';
import {
  Building2, MapPin, Globe, Save, Edit2, X, Plus, Trash2,
  CheckCircle, AlertCircle, ChevronDown, ChevronUp, Home, Route, Clock,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Collapsible, CollapsibleTrigger, CollapsibleContent,
} from '../components/ui/collapsible';
import { DEFAULT_COUNTRIES, DEFAULT_CITIES_BY_COUNTRY } from '../lib/countries';

// ─────────────────────────────────────────────────────────────
// Section Header (reusable)
// ─────────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, subtitle, color, isOpen }) => (
  <div className="flex items-center justify-between w-full">
    <div className="flex items-center gap-3">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${color}`}>
        <Icon size={22} />
      </div>
      <div>
        <h3 className="text-lg font-bold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
    {isOpen ? <ChevronUp size={22} className="text-muted-foreground" /> : <ChevronDown size={22} className="text-muted-foreground" />}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Read-only field display
// ─────────────────────────────────────────────────────────────
const ReadOnlyField = ({ value, fallback }) => (
  <div className="rounded-md border border-border bg-muted px-3 py-2.5 text-sm font-medium text-foreground">
    {value || fallback}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────
const CompanyInfoDynamic = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    info: true,
    headquarters: true,
    delivery: true,
    depots: true,
    loading: true,
    routes: false,
  });

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/company/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(t('errorFetching'));

      const data = await response.json();
      if (!data.general) data.general = {};
      if (!data.general.delivery_countries) data.general.delivery_countries = [];

      setSettings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/company/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          general: settings.general,
          transport: settings.transport,
          communication: settings.communication,
          financial: settings.financial,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = typeof errorData.detail === 'string'
          ? errorData.detail
          : JSON.stringify(errorData.detail) || t('errorSaving');
        throw new Error(errorMessage);
      }

      const updatedSettings = await response.json();
      setSettings(updatedSettings);
      setSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const errorMessage = err.message || String(err);
      setError(errorMessage);
      console.error('Erreur de sauvegarde:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateGeneralSettings = (field, value) => {
    setSettings({
      ...settings,
      general: { ...settings.general, [field]: value },
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // ── Delivery countries ──
  const addDeliveryCountry = () => {
    const currentCountries = settings.general.delivery_countries || [];
    updateGeneralSettings('delivery_countries', [
      ...currentCountries,
      { country_name: '', is_default: false, cities: [] },
    ]);
  };

  const updateDeliveryCountry = (index, field, value) => {
    const currentCountries = [...settings.general.delivery_countries];
    currentCountries[index] = { ...currentCountries[index], [field]: value };
    updateGeneralSettings('delivery_countries', currentCountries);
  };

  const removeDeliveryCountry = (index) => {
    updateGeneralSettings(
      'delivery_countries',
      settings.general.delivery_countries.filter((_, i) => i !== index),
    );
  };

  const addCity = (countryIndex) => {
    const currentCountries = [...settings.general.delivery_countries];
    if (!currentCountries[countryIndex].cities) {
      currentCountries[countryIndex].cities = [];
    }
    currentCountries[countryIndex].cities.push({ city_name: '', pickup_address: '' });
    updateGeneralSettings('delivery_countries', currentCountries);
  };

  const updateCity = (countryIndex, cityIndex, field, value) => {
    const currentCountries = [...settings.general.delivery_countries];
    currentCountries[countryIndex].cities[cityIndex] = {
      ...currentCountries[countryIndex].cities[cityIndex],
      [field]: value,
    };
    updateGeneralSettings('delivery_countries', currentCountries);
  };

  const removeCity = (countryIndex, cityIndex) => {
    const currentCountries = [...settings.general.delivery_countries];
    currentCountries[countryIndex].cities = currentCountries[countryIndex].cities.filter((_, i) => i !== cityIndex);
    updateGeneralSettings('delivery_countries', currentCountries);
  };

  // ── Headquarters ──
  const addHeadquarter = () => {
    const currentHeadquarters = settings.general.headquarters || [];
    updateGeneralSettings('headquarters', [
      ...currentHeadquarters,
      { country_name: '', country_code: '', cities: [] },
    ]);
  };

  const updateHeadquarter = (index, field, value) => {
    const currentHeadquarters = [...(settings.general.headquarters || [])];
    currentHeadquarters[index] = { ...currentHeadquarters[index], [field]: value };
    updateGeneralSettings('headquarters', currentHeadquarters);
  };

  const removeHeadquarter = (index) => {
    updateGeneralSettings(
      'headquarters',
      (settings.general.headquarters || []).filter((_, i) => i !== index),
    );
  };

  const addHeadquarterCity = (hqIndex) => {
    const currentHeadquarters = [...(settings.general.headquarters || [])];
    if (!currentHeadquarters[hqIndex].cities) {
      currentHeadquarters[hqIndex].cities = [];
    }
    currentHeadquarters[hqIndex].cities.push({ city_name: '', pickup_address: '' });
    updateGeneralSettings('headquarters', currentHeadquarters);
  };

  const updateHeadquarterCity = (hqIndex, cityIndex, field, value) => {
    const currentHeadquarters = [...(settings.general.headquarters || [])];
    currentHeadquarters[hqIndex].cities[cityIndex] = {
      ...currentHeadquarters[hqIndex].cities[cityIndex],
      [field]: value,
    };
    updateGeneralSettings('headquarters', currentHeadquarters);
  };

  const removeHeadquarterCity = (hqIndex, cityIndex) => {
    const currentHeadquarters = [...(settings.general.headquarters || [])];
    currentHeadquarters[hqIndex].cities = currentHeadquarters[hqIndex].cities.filter((_, i) => i !== cityIndex);
    updateGeneralSettings('headquarters', currentHeadquarters);
  };

  // ── Points de dépôt (warehouse_addresses) ──
  const addWarehouse = () => {
    const current = settings.general.warehouse_addresses || [];
    updateGeneralSettings('warehouse_addresses', [
      ...current,
      { name: '', address: '', city: '', country: '', phone: '' },
    ]);
  };

  const updateWarehouse = (index, field, value) => {
    const current = [...(settings.general.warehouse_addresses || [])];
    current[index] = { ...current[index], [field]: value };
    updateGeneralSettings('warehouse_addresses', current);
  };

  const removeWarehouse = (index) => {
    updateGeneralSettings(
      'warehouse_addresses',
      (settings.general.warehouse_addresses || []).filter((_, i) => i !== index),
    );
  };

  // ── Routes & Transit ──
  const updateTransportSettings = (field, value) => {
    setSettings({
      ...settings,
      transport: { ...settings.transport, [field]: value },
    });
  };

  const getRouteTransitConfigs = () => settings?.transport?.route_transit_configs || [];
  const getRoutePricing = () => settings?.financial?.route_pricing || [];

  const addRouteTransit = () => {
    const configs = [...getRouteTransitConfigs()];
    const hqs = settings?.general?.headquarters || [];
    const dels = settings?.general?.delivery_countries || [];
    configs.push({
      origin_country: hqs[0]?.code || hqs[0]?.country_code || '',
      destination_country: dels[0]?.code || dels[0]?.country_code || '',
      transit_duration_days: 7,
      transit_duration_max_days: null,
    });
    updateTransportSettings('route_transit_configs', configs);
  };

  const updateRouteTransit = (index, field, value) => {
    const configs = [...getRouteTransitConfigs()];
    configs[index] = { ...configs[index], [field]: value };
    updateTransportSettings('route_transit_configs', configs);
  };

  const removeRouteTransit = (index) => {
    updateTransportSettings(
      'route_transit_configs',
      getRouteTransitConfigs().filter((_, i) => i !== index),
    );
  };

  const getCombinedRoutes = () => {
    const transitConfigs = getRouteTransitConfigs();
    const pricing = getRoutePricing();
    const routeMap = {};

    transitConfigs.forEach(tc => {
      const key = `${tc.origin_country}-${tc.destination_country}`;
      routeMap[key] = {
        origin: tc.origin_country,
        destination: tc.destination_country,
        transit_days: tc.transit_duration_days,
        transit_max_days: tc.transit_duration_max_days,
        rate_per_kg: null,
        currency: null,
      };
    });

    pricing.forEach(p => {
      const key = `${p.origin_country}-${p.destination_country}`;
      if (routeMap[key]) {
        routeMap[key].rate_per_kg = p.rate_per_kg;
        routeMap[key].currency = p.currency;
      } else {
        routeMap[key] = {
          origin: p.origin_country,
          destination: p.destination_country,
          transit_days: null,
          transit_max_days: null,
          rate_per_kg: p.rate_per_kg,
          currency: p.currency,
        };
      }
    });

    return Object.values(routeMap);
  };

  // ── Loading / Empty states ──
  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-accent" />
        </div>
      </ProfessionalLayout>
    );
  }

  if (!settings) {
    return (
      <ProfessionalLayout>
        <div className="flex flex-col items-center justify-center min-h-[300px] gap-3 text-muted-foreground">
          <AlertCircle size={48} />
          <h3 className="text-lg font-semibold text-muted-foreground">{t('noSettingsFound')}</h3>
        </div>
      </ProfessionalLayout>
    );
  }

  const companyName = settings.general?.company_name || '';
  const companyType = settings.general?.company_type || 'freight_forwarder';
  const deliveryCountries = settings.general?.delivery_countries || [];
  const headquartersList = settings.general?.headquarters || [];

  const companyTypeLabels = {
    freight_forwarder: t('freightForwarder'),
    carrier: t('carrier'),
    logistics_provider: t('logisticsProvider'),
    customs_broker: t('customsBroker'),
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <ProfessionalLayout>
      {/* ── Header ── */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-3 text-2xl font-bold text-foreground">
              <Building2 size={32} className="text-amber-500" />
              {t('companyInfo')}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('companyInfoDesc')}
            </p>
          </div>
          <div className="flex gap-2">
            {!isEditing ? (
              <Button onClick={() => setIsEditing(true)} className="gap-2">
                <Edit2 size={16} />
                {t('edit')}
              </Button>
            ) : (
              <>
                <Button onClick={handleSave} disabled={saving} className="gap-2">
                  <Save size={16} />
                  {saving ? t('saving') : t('save')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setIsEditing(false); fetchSettings(); }}
                  disabled={saving}
                  className="gap-2"
                >
                  <X size={16} />
                  {t('cancel')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Alerts ── */}
      {success && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border-2 border-emerald-500 bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">
          <CheckCircle size={18} />
          {t('settingsSavedSuccess')}
        </div>
      )}
      {error && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border-2 border-red-500 bg-red-50 p-3 text-sm font-semibold text-red-700">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* ── Sections ── */}
      <div className="space-y-6">

        {/* ════════════════════════════════════════════════════════
            Section 1: Informations Générales
           ════════════════════════════════════════════════════════ */}
        <Collapsible open={expandedSections.info} onOpenChange={() => toggleSection('info')}>
          <Card className="border-l-4 border-l-amber-400">
            <CollapsibleTrigger asChild>
              <CardContent className="cursor-pointer pt-6 pb-4">
                <SectionHeader
                  icon={Building2}
                  title={t('generalInformation')}
                  subtitle={t('companyNameAndType')}
                  color="bg-amber-100 text-amber-600"
                  isOpen={expandedSections.info}
                />
              </CardContent>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                <Separator />

                <div>
                  <Label className="mb-1.5 block font-semibold">{t('companyName')}</Label>
                  {isEditing ? (
                    <Input
                      value={companyName}
                      onChange={(e) => updateGeneralSettings('company_name', e.target.value)}
                      placeholder={t('companyName')}
                    />
                  ) : (
                    <ReadOnlyField value={companyName} fallback={t('notDefined')} />
                  )}
                </div>

                <div>
                  <Label className="mb-1.5 block font-semibold">{t('companyType')}</Label>
                  {isEditing ? (
                    <Select value={companyType} onValueChange={(val) => updateGeneralSettings('company_type', val)}>
                      <SelectTrigger className="w-full max-w-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freight_forwarder">{t('freightForwarder')}</SelectItem>
                        <SelectItem value="carrier">{t('carrier')}</SelectItem>
                        <SelectItem value="logistics_provider">{t('logisticsProvider')}</SelectItem>
                        <SelectItem value="customs_broker">{t('customsBroker')}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <ReadOnlyField value={companyTypeLabels[companyType]} fallback={t('notDefined')} />
                  )}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Sections Sièges & Pays de livraison masquées : la configuration passe
            désormais par les listes déroulantes (pays/villes prédéfinis). */}
        {false && (<>
        {/* ════════════════════════════════════════════════════════
            Section 2: Sièges (Pays d'Expédition)
           ════════════════════════════════════════════════════════ */}
        <Collapsible open={expandedSections.headquarters} onOpenChange={() => toggleSection('headquarters')}>
          <Card className="border-l-4 border-l-blue-400">
            <CollapsibleTrigger asChild>
              <CardContent className="cursor-pointer pt-6 pb-4">
                <SectionHeader
                  icon={Home}
                  title={t('headquartersShipping')}
                  subtitle={`${headquartersList.length} ${headquartersList.length > 1 ? t('headquartersConfiguredPlural') : t('headquartersConfiguredSingular')}`}
                  color="bg-accent/10 text-accent"
                  isOpen={expandedSections.headquarters}
                />
              </CardContent>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                <Separator />

                {headquartersList.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 rounded-lg bg-muted py-8 text-center">
                    <Home size={40} className="text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{t('noHeadquartersConfigured')}</p>
                    {isEditing && (
                      <Button onClick={addHeadquarter} size="sm" className="gap-2">
                        <Plus size={16} />
                        {t('addHeadquarter')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {headquartersList.map((hq, hqIndex) => (
                      <Card key={hqIndex} className="border-2 border-accent/20 bg-blue-50/50">
                        <CardContent className="pt-4 space-y-3">
                          {/* HQ header */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {isEditing ? (
                                <Select
                                  value={hq.country_name || hq.name || ''}
                                  onValueChange={(val) => {
                                    const country = DEFAULT_COUNTRIES.find(c => c.name === val);
                                    updateHeadquarter(hqIndex, 'country_name', val);
                                    if (country) updateHeadquarter(hqIndex, 'country_code', country.code);
                                  }}
                                >
                                  <SelectTrigger className="font-semibold">
                                    <SelectValue placeholder={t('selectCountryHQ')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DEFAULT_COUNTRIES.map(c => (
                                      <SelectItem key={c.code} value={c.name}>{c.name} ({c.code})</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Home size={18} className="text-accent" />
                                  <span className="text-base font-bold text-foreground">
                                    {hq.country_name || hq.name} {hq.country_code || hq.code ? `(${hq.country_code || hq.code})` : ''}
                                  </span>
                                  <Badge className="bg-accent text-white text-xs">SIÈGE</Badge>
                                </div>
                              )}
                            </div>
                            {isEditing && (
                              <Button variant="ghost" size="icon" onClick={() => removeHeadquarter(hqIndex)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>

                          {/* HQ cities */}
                          {hq.cities && hq.cities.length > 0 && (
                            <div>
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {t('cities')} ({hq.cities.length})
                              </p>
                              <div className="space-y-2">
                                {hq.cities.map((city, cityIndex) => (
                                  <div key={cityIndex} className="flex items-start justify-between gap-2 rounded-md border border-border bg-white p-3">
                                    <div className="flex-1">
                                      {isEditing ? (
                                        <div className="space-y-2">
                                          {(() => {
                                            const hqCode = hq.country_code || hq.code;
                                            const cities = hqCode ? (DEFAULT_CITIES_BY_COUNTRY[hqCode] || []) : [];
                                            if (cities.length > 0) {
                                              return (
                                                <Select
                                                  value={city.city_name || city.name || ''}
                                                  onValueChange={(val) => updateHeadquarterCity(hqIndex, cityIndex, 'city_name', val)}
                                                >
                                                  <SelectTrigger className="font-semibold text-sm">
                                                    <SelectValue placeholder={t('selectCity')} />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {cities.map(c => (
                                                      <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                                                    ))}
                                                  </SelectContent>
                                                </Select>
                                              );
                                            }
                                            return (
                                              <Input
                                                value={city.city_name || city.name || ''}
                                                onChange={(e) => updateHeadquarterCity(hqIndex, cityIndex, 'city_name', e.target.value)}
                                                placeholder={t('cityName')}
                                                className="font-semibold text-sm"
                                              />
                                            );
                                          })()}
                                          <Textarea
                                            value={city.pickup_address || city.address || ''}
                                            onChange={(e) => updateHeadquarterCity(hqIndex, cityIndex, 'pickup_address', e.target.value)}
                                            placeholder={t('headquarterAddress')}
                                            rows={2}
                                            className="text-sm"
                                          />
                                        </div>
                                      ) : (
                                        <div>
                                          <div className="flex items-center gap-2 mb-1">
                                            <MapPin size={14} className="text-accent" />
                                            <span className="text-sm font-semibold text-foreground">
                                              {city.city_name || city.name}
                                            </span>
                                          </div>
                                          {(city.pickup_address || city.address) && (
                                            <p className="ml-6 rounded border-l-2 border-blue-400 bg-muted px-3 py-1.5 text-xs text-muted-foreground">
                                              {city.pickup_address || city.address}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {isEditing && (
                                      <Button variant="ghost" size="icon" onClick={() => removeHeadquarterCity(hqIndex, cityIndex)} className="shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8">
                                        <X size={14} />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {isEditing && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addHeadquarterCity(hqIndex)}
                              className="w-full gap-2 border-dashed text-muted-foreground"
                            >
                              <Plus size={14} />
                              {t('addCity')}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))}

                    {isEditing && (
                      <Button variant="outline" onClick={addHeadquarter} className="w-full gap-2">
                        <Plus size={16} />
                        {t('addHeadquarter')}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* ════════════════════════════════════════════════════════
            Section 3: Pays de Livraison
           ════════════════════════════════════════════════════════ */}
        <Collapsible open={expandedSections.delivery} onOpenChange={() => toggleSection('delivery')}>
          <Card className="border-l-4 border-l-emerald-400">
            <CollapsibleTrigger asChild>
              <CardContent className="cursor-pointer pt-6 pb-4">
                <SectionHeader
                  icon={Globe}
                  title={t('deliveryCountries')}
                  subtitle={`${deliveryCountries.length} ${deliveryCountries.length > 1 ? t('countriesConfiguredPlural') : t('countriesConfiguredSingular')}`}
                  color="bg-emerald-100 text-emerald-600"
                  isOpen={expandedSections.delivery}
                />
              </CardContent>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                <Separator />

                {deliveryCountries.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 rounded-lg bg-muted py-8 text-center">
                    <Globe size={40} className="text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{t('noDeliveryCountriesConfigured')}</p>
                    {isEditing && (
                      <Button onClick={addDeliveryCountry} size="sm" className="gap-2">
                        <Plus size={16} />
                        {t('addCountry')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {deliveryCountries.map((country, countryIndex) => (
                      <Card
                        key={countryIndex}
                        className={country.is_default
                          ? 'border-2 border-amber-300 bg-amber-50/50'
                          : 'border border-border'}
                      >
                        <CardContent className="pt-4 space-y-3">
                          {/* Country header */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {isEditing ? (
                                <Select
                                  value={country.country_name || country.name || ''}
                                  onValueChange={(val) => {
                                    const c = DEFAULT_COUNTRIES.find(x => x.name === val);
                                    updateDeliveryCountry(countryIndex, 'country_name', val);
                                    if (c) updateDeliveryCountry(countryIndex, 'country_code', c.code);
                                  }}
                                >
                                  <SelectTrigger className="font-semibold">
                                    <SelectValue placeholder={t('selectCountry')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DEFAULT_COUNTRIES.map(c => (
                                      <SelectItem key={c.code} value={c.name}>{c.name} ({c.code})</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Globe size={18} className={country.is_default ? 'text-amber-500' : 'text-emerald-500'} />
                                  <span className="text-base font-bold text-foreground">
                                    {country.country_name || country.name} {country.country_code || country.code ? `(${country.country_code || country.code})` : ''}
                                  </span>
                                  {country.is_default && (
                                    <Badge className="bg-amber-500 text-white text-xs">SIÈGE</Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            {isEditing && !country.is_default && (
                              <Button variant="ghost" size="icon" onClick={() => removeDeliveryCountry(countryIndex)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>

                          {/* Cities */}
                          {country.cities && country.cities.length > 0 && (
                            <div>
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {t('cities')} ({country.cities.length})
                              </p>
                              <div className="space-y-2">
                                {country.cities.map((city, cityIndex) => (
                                  <div key={cityIndex} className="flex items-start justify-between gap-2 rounded-md border border-border bg-white p-3">
                                    <div className="flex-1">
                                      {isEditing ? (
                                        <div className="space-y-2">
                                          {(() => {
                                            const cCode = country.country_code || country.code;
                                            const cities = cCode ? (DEFAULT_CITIES_BY_COUNTRY[cCode] || []) : [];
                                            if (cities.length > 0) {
                                              return (
                                                <Select
                                                  value={city.city_name || city.name || ''}
                                                  onValueChange={(val) => updateCity(countryIndex, cityIndex, 'city_name', val)}
                                                >
                                                  <SelectTrigger className="font-semibold text-sm">
                                                    <SelectValue placeholder={t('selectCity')} />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {cities.map(c => (
                                                      <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                                                    ))}
                                                  </SelectContent>
                                                </Select>
                                              );
                                            }
                                            return (
                                              <Input
                                                value={city.city_name || city.name || ''}
                                                onChange={(e) => updateCity(countryIndex, cityIndex, 'city_name', e.target.value)}
                                                placeholder={t('cityName')}
                                                className="font-semibold text-sm"
                                              />
                                            );
                                          })()}
                                          <Textarea
                                            value={city.pickup_address || city.address || ''}
                                            onChange={(e) => updateCity(countryIndex, cityIndex, 'pickup_address', e.target.value)}
                                            placeholder={t('pickupAddress')}
                                            rows={2}
                                            className="text-sm"
                                          />
                                        </div>
                                      ) : (
                                        <div>
                                          <div className="flex items-center gap-2 mb-1">
                                            <MapPin size={14} className="text-amber-500" />
                                            <span className="text-sm font-semibold text-foreground">
                                              {city.city_name || city.name}
                                            </span>
                                          </div>
                                          {(city.pickup_address || city.address) && (
                                            <p className="ml-6 rounded border-l-2 border-amber-400 bg-muted px-3 py-1.5 text-xs text-muted-foreground">
                                              {city.pickup_address || city.address}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {isEditing && (
                                      <Button variant="ghost" size="icon" onClick={() => removeCity(countryIndex, cityIndex)} className="shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8">
                                        <X size={14} />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {isEditing && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addCity(countryIndex)}
                              className="w-full gap-2 border-dashed text-muted-foreground"
                            >
                              <Plus size={14} />
                              {t('addCity')}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))}

                    {isEditing && (
                      <Button variant="outline" onClick={addDeliveryCountry} className="w-full gap-2">
                        <Plus size={16} />
                        {t('addAdditionalCountry')}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
        </>)}

        {/* ════════════════════════════════════════════════════════
            Section : Points de dépôt (visibles sur le calendrier public)
           ════════════════════════════════════════════════════════ */}
        <Collapsible open={expandedSections.depots} onOpenChange={() => toggleSection('depots')}>
          <Card className="border-l-4 border-l-orange-400">
            <CollapsibleTrigger asChild>
              <CardContent className="cursor-pointer pt-6 pb-4">
                <SectionHeader
                  icon={MapPin}
                  title={t('depotPoints') || 'Points de dépôt'}
                  subtitle={`${(settings.general.warehouse_addresses || []).length} ${t('depotPointsSubtitle') || 'point(s) — visibles sur le calendrier public'}`}
                  color="bg-orange-500/10 text-orange-500"
                  isOpen={expandedSections.depots}
                />
              </CardContent>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                <Separator />
                {(settings.general.warehouse_addresses || []).length === 0 ? (
                  <div className="flex flex-col items-center gap-3 rounded-lg bg-muted py-8 text-center">
                    <MapPin size={40} className="text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{t('noDepotPoints') || 'Aucun point de dépôt configuré'}</p>
                    {isEditing && (
                      <Button onClick={addWarehouse} size="sm" className="gap-2">
                        <Plus size={16} /> {t('add') || 'Ajouter'}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(settings.general.warehouse_addresses || []).map((w, index) => (
                      <div key={index} className="rounded-lg border border-border p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-foreground">{w.name || `${t('depotPoint') || 'Point de dépôt'} ${index + 1}`}</span>
                          {isEditing && (
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => removeWarehouse(index)}>
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="mb-1 block text-xs">{t('name') || 'Nom'}</Label>
                              <Input value={w.name || ''} onChange={(e) => updateWarehouse(index, 'name', e.target.value)} placeholder="Entrepôt Douala" />
                            </div>
                            <div>
                              <Label className="mb-1 block text-xs">{t('phone') || 'Téléphone'}</Label>
                              <Input value={w.phone || ''} onChange={(e) => updateWarehouse(index, 'phone', e.target.value)} placeholder="+237 6 00 00 00 00" />
                            </div>
                            <div className="sm:col-span-2">
                              <Label className="mb-1 block text-xs">{t('address') || 'Adresse'}</Label>
                              <Input value={w.address || ''} onChange={(e) => updateWarehouse(index, 'address', e.target.value)} placeholder="123 Rue du Port" />
                            </div>
                            <div>
                              <Label className="mb-1 block text-xs">{t('city') || 'Ville'}</Label>
                              <Input value={w.city || ''} onChange={(e) => updateWarehouse(index, 'city', e.target.value)} placeholder="Douala" />
                            </div>
                            <div>
                              <Label className="mb-1 block text-xs">{t('country') || 'Pays'}</Label>
                              <Input value={w.country || ''} onChange={(e) => updateWarehouse(index, 'country', e.target.value)} placeholder="Cameroun" />
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-muted-foreground space-y-0.5">
                            {(w.address || w.city || w.country) && <div>{[w.address, w.city, w.country].filter(Boolean).join(', ')}</div>}
                            {w.phone && <div>{w.phone}</div>}
                          </div>
                        )}
                      </div>
                    ))}
                    {isEditing && (
                      <Button onClick={addWarehouse} size="sm" variant="outline" className="gap-2">
                        <Plus size={16} /> {t('add') || 'Ajouter un point de dépôt'}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* ════════════════════════════════════════════════════════
            Section 4: Routes & Transit
           ════════════════════════════════════════════════════════ */}
        <Collapsible open={expandedSections.routes} onOpenChange={() => toggleSection('routes')}>
          <Card className="border-l-4 border-l-violet-400">
            <CollapsibleTrigger asChild>
              <CardContent className="cursor-pointer pt-6 pb-4">
                <SectionHeader
                  icon={Route}
                  title={t('routesAndTransit')}
                  subtitle={t('transitDurationsAndRates')}
                  color="bg-violet-100 text-violet-600"
                  isOpen={expandedSections.routes}
                />
              </CardContent>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                <Separator />

                {/* Read-only table view */}
                {!isEditing && (
                  <>
                    {getCombinedRoutes().length === 0 ? (
                      <div className="flex flex-col items-center gap-3 rounded-lg bg-muted py-8 text-center">
                        <Route size={40} className="text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">{t('noRoutesConfigured')}</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-muted">
                              <th className="px-4 py-2.5 text-left font-semibold text-muted-foreground">{t('origin')}</th>
                              <th className="px-4 py-2.5 text-left font-semibold text-muted-foreground">{t('destination')}</th>
                              <th className="px-4 py-2.5 text-center font-semibold text-muted-foreground">{t('transitDays')}</th>
                              <th className="px-4 py-2.5 text-right font-semibold text-muted-foreground">{t('pricePerKg')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getCombinedRoutes().map((r, idx) => (
                              <tr key={idx} className="border-b border-border last:border-0">
                                <td className="px-4 py-2.5 font-medium text-foreground">{r.origin}</td>
                                <td className="px-4 py-2.5 font-medium text-foreground">{r.destination}</td>
                                <td className="px-4 py-2.5 text-center">
                                  {r.transit_days != null ? (
                                    <Badge variant="secondary" className="gap-1 bg-violet-100 text-violet-700">
                                      <Clock size={12} />
                                      {r.transit_days}{r.transit_max_days ? `–${r.transit_max_days}` : ''} j
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-right font-semibold text-foreground">
                                  {r.rate_per_kg != null ? (
                                    <span>{r.rate_per_kg} {r.currency || 'EUR'}</span>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {/* Edit mode: transit configs */}
                {isEditing && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-muted-foreground">{t('transitDurationsByRoute')}</p>
                      <Button onClick={addRouteTransit} size="sm" className="gap-2">
                        <Plus size={14} />
                        {t('add')}
                      </Button>
                    </div>

                    {getRouteTransitConfigs().length === 0 ? (
                      <div className="rounded-lg bg-muted py-6 text-center">
                        <p className="text-sm text-muted-foreground">{t('noTransitConfig')}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {getRouteTransitConfigs().map((config, idx) => {
                          const hqs = settings?.general?.headquarters || [];
                          const dels = settings?.general?.delivery_countries || [];
                          return (
                            <div key={idx} className="flex flex-wrap items-center gap-3 rounded-lg border border-violet-200 bg-violet-50/50 p-3">
                              <Select
                                value={config.origin_country}
                                onValueChange={(val) => updateRouteTransit(idx, 'origin_country', val)}
                              >
                                <SelectTrigger className="w-40 bg-white">
                                  <SelectValue placeholder="Origine" />
                                </SelectTrigger>
                                <SelectContent>
                                  {hqs.map((h, i) => (
                                    <SelectItem key={i} value={h.code || h.country_code || `hq-${i}`}>
                                      {h.name || h.country_name} ({h.code || h.country_code})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              <span className="font-bold text-muted-foreground">→</span>

                              <Select
                                value={config.destination_country}
                                onValueChange={(val) => updateRouteTransit(idx, 'destination_country', val)}
                              >
                                <SelectTrigger className="w-40 bg-white">
                                  <SelectValue placeholder="Destination" />
                                </SelectTrigger>
                                <SelectContent>
                                  {dels.map((d, i) => (
                                    <SelectItem key={i} value={d.code || d.country_code || `del-${i}`}>
                                      {d.name || d.country_name} ({d.code || d.country_code})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              <div className="flex items-center gap-1.5">
                                <Input
                                  type="number"
                                  min="1"
                                  value={config.transit_duration_days || ''}
                                  onChange={(e) => updateRouteTransit(idx, 'transit_duration_days', parseInt(e.target.value) || 0)}
                                  placeholder="Min"
                                  className="w-16 text-center bg-white"
                                />
                                <span className="text-sm text-muted-foreground">–</span>
                                <Input
                                  type="number"
                                  min="1"
                                  value={config.transit_duration_max_days || ''}
                                  onChange={(e) => updateRouteTransit(idx, 'transit_duration_max_days', parseInt(e.target.value) || null)}
                                  placeholder="Max"
                                  className="w-16 text-center bg-white"
                                />
                                <span className="text-xs text-muted-foreground">{t('days')}</span>
                              </div>

                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeRouteTransit(idx)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      {t('routePricingNote')}
                    </p>
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    </ProfessionalLayout>
  );
};

export default CompanyInfoDynamic;
